import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Package } from 'lucide-react';
import AppIcon from '../components/AppIcon';
import { PageSeo } from '../components/Seo';
import { getErrorMessage } from '../api/errors';
import { preOrdersService } from '../services/preOrders/preOrdersService';
import type { PreOrder } from '../types';
import { getProductUrl } from '../utils/product';
import { PRE_ORDER_STATUS_LABELS } from '../utils/preOrders';

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('tr-TR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function PreOrders() {
  const [preOrders, setPreOrders] = useState<PreOrder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const loadPreOrders = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await preOrdersService.fetchPreOrders();
      setPreOrders(data);
    } catch (err) {
      setError(getErrorMessage(err, 'Ön siparişler yüklenemedi.'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadPreOrders();
  }, [loadPreOrders]);

  return (
    <div className="container orders-page pre-orders-page">
      <PageSeo title="Ön Siparişlerim" noindex />

      <div className="orders-page-header">
        <div>
          <h1>Ön Siparişlerim</h1>
          <p>Stokta olmayan ürünler için oluşturduğunuz talepler</p>
        </div>
        <button type="button" className="btn btn-outline" onClick={() => void loadPreOrders()}>
          Yenile
        </button>
      </div>

      {error && (
        <div className="auth-error">
          {error}
          <button type="button" className="btn btn-outline btn-sm" onClick={() => void loadPreOrders()}>
            Tekrar Dene
          </button>
        </div>
      )}

      {isLoading ? (
        <p>Yükleniyor...</p>
      ) : preOrders.length === 0 ? (
        <div className="empty-state">
          <span className="empty-icon">
            <AppIcon icon={Package} size={64} strokeWidth={1.5} />
          </span>
          <h2>Henüz ön siparişiniz yok</h2>
          <p>Stokta olmayan ürünlerde &quot;Ön Sipariş Oluştur&quot; butonunu kullanabilirsiniz.</p>
          <Link to="/shop" className="btn btn-primary">Ürünlere Git</Link>
        </div>
      ) : (
        <div className="pre-orders-list">
          {preOrders.map((preOrder) => (
            <article key={preOrder.id} className="pre-order-card">
              <div className="pre-order-card-header">
                <div>
                  <Link to={getProductUrl(preOrder.productSlug)} className="pre-order-product-link">
                    {preOrder.productName}
                  </Link>
                  <span className="order-date">{formatDate(preOrder.createdAt)}</span>
                </div>
                <span className={`order-status status-${preOrder.status}`}>
                  {PRE_ORDER_STATUS_LABELS[preOrder.status]}
                </span>
              </div>

              <div className="pre-order-card-body">
                <p>
                  <strong>Adet:</strong> {preOrder.quantity}
                </p>
                <p>
                  <strong>Telefon:</strong> {preOrder.contact.phone}
                </p>
                {preOrder.note && (
                  <p className="pre-order-note">
                    <strong>Not:</strong> {preOrder.note}
                  </p>
                )}
              </div>

              {preOrder.status === 'pending' && (
                <p className="pre-order-pending-hint">
                  <AppIcon icon={Clock} size={16} />
                  Stok geldiğinde sizi bilgilendireceğiz.
                </p>
              )}
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
